/* ==========================================================================
   BANCO — Animations globales (aucune dépendance, ~3 Ko gzip)
   Charger en defer dans theme.liquid :
   <script src="{{ 'banco-animations.js' | asset_url }}" defer></script>

   - .banco-reveal          : fade + slide-up au scroll (stagger via --reveal-delay)
   - [data-banco-counter]   : compteur animé (data-target, data-suffix)
   - [data-banco-parallax]  : parallax subtil (valeur = vitesse, ex. "0.1")
   - [data-banco-magnetic]  : bouton magnétique (desktop pointeur fin uniquement)
   - [data-banco-quick-add] : ajout panier en 1 clic (valeur = id de variante)
   - [data-banco-choix]     : le + d'un produit à plusieurs variantes — ouvre la
                              liste posée dans le <template> voisin, et c'est
                              l'option choisie qui porte le data-banco-quick-add
   Tout est désactivé par prefers-reduced-motion et inerte sans JS.
   ========================================================================== */
(function () {
  'use strict';

  document.documentElement.classList.add('banco-js');

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------------- Révélation au scroll ---------------- */
  var revealObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

  function initReveals(root) {
    (root || document).querySelectorAll('.banco-reveal:not(.is-visible)').forEach(function (el) {
      if (reduced) { el.classList.add('is-visible'); return; }
      revealObserver.observe(el);
    });
  }

  /* Garde-fou : si pour une raison quelconque l'observer n'a pas révélé un
     élément pourtant à l'écran (onglet chargé en arrière-plan, etc.),
     on force l'affichage après 2,5 s. Répété au retour sur l'onglet. */
  function forceVisibleInViewport() {
    var vh = window.innerHeight;
    document.querySelectorAll('.banco-reveal:not(.is-visible)').forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) el.classList.add('is-visible');
    });
  }
  setTimeout(forceVisibleInViewport, 2500);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) setTimeout(forceVisibleInViewport, 300);
  });

  /* ---------------- Compteurs animés ---------------- */
  var fmt = new Intl.NumberFormat('fr-FR');

  function animateCounter(el) {
    var target = parseFloat(String(el.dataset.target || '0').replace(/\s/g, '').replace(',', '.'));
    var suffix = el.dataset.suffix || '';
    var decimals = (String(el.dataset.target).split(/[.,]/)[1] || '').length;
    if (reduced || !isFinite(target)) {
      el.textContent = fmt.format(target) + suffix;
      return;
    }
    var duration = 1400;
    var start = null;
    function tick(now) {
      if (start === null) start = now;
      var p = Math.min(1, (now - start) / duration);
      var eased = 1 - Math.pow(1 - p, 3); /* easeOutCubic */
      var value = target * eased;
      el.textContent = fmt.format(decimals ? value.toFixed(decimals) : Math.round(value)) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  var counterObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        animateCounter(entry.target);
        counterObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.6 });

  function initCounters(root) {
    (root || document).querySelectorAll('[data-banco-counter]').forEach(function (el) {
      counterObserver.observe(el);
    });
  }

  /* ---------------- Parallax subtil ---------------- */
  var parallaxEls = [];
  var parallaxTicking = false;

  function parallaxFrame() {
    parallaxTicking = false;
    var vh = window.innerHeight;
    parallaxEls.forEach(function (item) {
      var rect = item.el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) return;
      var progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2);
      var shift = Math.max(-1, Math.min(1, progress)) * item.speed * -100;
      item.el.style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0)';
    });
  }

  function onParallaxScroll() {
    if (!parallaxTicking) {
      parallaxTicking = true;
      requestAnimationFrame(parallaxFrame);
    }
  }

  function initParallax(root) {
    if (reduced) return;
    (root || document).querySelectorAll('[data-banco-parallax]').forEach(function (el) {
      if (parallaxEls.some(function (i) { return i.el === el; })) return;
      parallaxEls.push({ el: el, speed: parseFloat(el.dataset.bancoParallax) || 0.1 });
    });
    if (parallaxEls.length && !initParallax.bound) {
      initParallax.bound = true;
      window.addEventListener('scroll', onParallaxScroll, { passive: true });
      window.addEventListener('resize', onParallaxScroll, { passive: true });
      onParallaxScroll();
    }
  }

  /* ---------------- Boutons magnétiques ---------------- */
  function initMagnetic(root) {
    if (reduced || !finePointer) return;
    (root || document).querySelectorAll('[data-banco-magnetic]').forEach(function (btn) {
      if (btn.bancoMagnetic) return;
      btn.bancoMagnetic = true;
      var strength = 0.18;
      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        var dx = (e.clientX - r.left - r.width / 2) * strength;
        var dy = (e.clientY - r.top - r.height / 2) * strength;
        btn.style.transform = 'translate(' + dx.toFixed(1) + 'px,' + dy.toFixed(1) + 'px)';
      });
      btn.addEventListener('pointerleave', function () {
        btn.style.transition = 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)';
        btn.style.transform = '';
        setTimeout(function () { btn.style.transition = ''; }, 350);
      });
    });
  }

  /* ---------------- Ajout panier en 1 clic ---------------- */
  /* Mise en scène de l'ajout : le vol de l'article jusqu'au panier, l'accusé
     de réception, le compteur. Assurée par banco-ajout-panier.js — commune à
     tous les chemins d'ajout du site. Repli si le fichier n'est pas là :
     le compteur est quand même remis à jour. */
  /* `apres` est appelé quand l'article s'est posé dans le panier : c'est là
     qu'on ouvre la confirmation, pas avant. Le client voit son article partir,
     puis la porte vers le paiement s'ouvrir — dans cet ordre. */
  function celebrerAjout(btn, sections, apres) {
    if (window.BancoPanier) {
      /* `coche: false` : ce bouton-là bascule déjà son + en ✓ tout seul
         (voir .banco-card__add.is-added dans banco-grille-produits.liquid) */
      window.BancoPanier
        .celebrer({ bouton: btn, sections: sections, coche: false })
        .then(function () { if (apres) apres(); });
      return;
    }
    if (apres) setTimeout(apres, 0);
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !sections || !sections['cart-icon-bubble']) return;
    var doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
    var section = doc.querySelector('.shopify-section') || doc.body;
    bubble.innerHTML = section.innerHTML;
    bubble.classList.add('banco-bump');
    setTimeout(function () { bubble.classList.remove('banco-bump'); }, 600);
  }

  /* ---------------- Ajout au panier depuis une carte ----------------
     `declencheur` est le bouton réellement cliqué (le + de la carte, ou une
     option de la liste) ; `hote` est toujours le + de la carte : c'est lui qui
     porte l'attente et la coche, et c'est de sa carte que part la vignette qui
     vole vers le panier. Depuis une option, `declencheur` vit sur <body>, loin
     de toute photo d'article — l'animation n'y trouverait rien à faire voler. */
  /* L'élément du thème qui sait ouvrir la confirmation d'ajout : la pastille
     Dawn (réglage `cart_type = notification`, celui de la boutique) ou le
     tiroir. Absent sur certaines pages — l'ajout marche quand même. */
  function panierDuTheme() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  /* Ouvre la confirmation du thème — celle qui porte « Voir le panier » et
     « Procéder au paiement ». Avant le 07/09/2026, un ajout depuis une grille
     ne montrait QUE le compteur qui monte : le client n'avait aucune route
     vers la caisse, et deux paniers sur trois n'y arrivaient jamais. */
  function ouvrirConfirmation(panier, reponse, hote) {
    if (!panier || typeof panier.renderContents !== 'function') return false;
    if (!reponse || !reponse.sections) return false;
    try {
      /* pour que la fermeture rende le focus au + d'où l'on vient */
      if (typeof panier.setActiveElement === 'function') panier.setActiveElement(hote);
      panier.renderContents(reponse);
      return true;
    } catch (e) {
      /* un fragment manquant ne doit pas passer pour un échec d'ajout :
         l'article est au panier, seule la mise en scène a manqué */
      return false;
    }
  }

  /* Vers où emmener le client si l'ajout échoue. Sur une carte d'accueil le
     bouton vit dans le lien ; sur une carte Dawn il n'y est pas (il passerait
     sous le lien invisible) — on remonte alors à la carte pour y lire le lien. */
  function lienProduit(hote) {
    var direct = hote.closest('a');
    if (direct && direct.href) return direct.href;
    var carte = hote.closest('.card-wrapper, .banco-card, .banco-edl__carte, .banco-grid__item');
    var lien = carte && carte.querySelector('a[href]');
    return lien ? lien.href : null;
  }

  function ajouterAuPanier(declencheur, hote, variante) {
    if (hote.classList.contains('is-loading') || hote.classList.contains('is-added')) return;
    hote.classList.add('is-loading');
    if (declencheur !== hote) declencheur.classList.add('is-loading');

    var panier = panierDuTheme();

    /* Même charge utile que le formulaire de la fiche produit (product-form.js) :
       en FormData, et non en JSON. C'est ce qui fait répondre à /cart/add.js la
       ligne ajoutée avec sa `key` — la pastille s'en sert pour retrouver le
       fragment de l'article. En JSON (`items: [...]`) la réponse n'a pas de
       `key`, et la confirmation s'ouvrirait sur une vignette vide. */
    var corps = new FormData();
    corps.append('id', variante);
    corps.append('quantity', 1);
    var fragments = ['cart-icon-bubble'];
    if (panier && typeof panier.getSectionsToRender === 'function') {
      fragments = panier.getSectionsToRender().map(function (s) { return s.id; });
    }
    corps.append('sections', fragments.join(','));
    corps.append('sections_url', window.location.pathname);

    fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: corps
    })
      .then(function (r) { if (!r.ok) throw new Error('add-to-cart'); return r.json(); })
      .then(function (d) {
        /* Shopify répond 200 en refusant (article épuisé) : le refus est dans
           `status`, pas dans le code HTTP. */
        if (d && d.status) throw new Error(d.description || 'add-to-cart');
        fermerChoix();
        hote.classList.remove('is-loading');
        hote.classList.add('is-added');
        celebrerAjout(hote, d.sections, function () {
          ouvrirConfirmation(panier, d, hote);
        });
        setTimeout(function () { hote.classList.remove('is-added'); }, 2200);
      })
      .catch(function () {
        /* en cas d'échec silencieux, on emmène vers la page produit */
        fermerChoix();
        hote.classList.remove('is-loading');
        var url = lienProduit(hote);
        if (url) window.location.href = url;
      });
  }

  /* ---------------- La liste des variantes ----------------
     Un produit à plusieurs motifs ou tailles ne peut pas s'ajouter en un clic
     sans choix : le + déplie la liste, et l'ajout part de l'option. La liste
     est posée sur <body> en position fixe — dans la carte, elle serait rognée
     par la grille — et se replace au défilement comme au redimensionnement. */
  var choixOuvert = null;

  function fermerChoix() {
    if (!choixOuvert) return;
    var ouvert = choixOuvert;
    choixOuvert = null;
    ouvert.hote.setAttribute('aria-expanded', 'false');
    if (ouvert.panneau.parentNode) ouvert.panneau.parentNode.removeChild(ouvert.panneau);
    window.removeEventListener('scroll', placerChoix, true);
    window.removeEventListener('resize', placerChoix);
  }

  /* Le panneau se colle au bouton : au-dessus s'il y a la place (c'est le cas
     ordinaire, le + étant en bas de la photo), sinon en dessous. Aligné à
     droite sur le bouton, puis ramené dans l'écran si le bord le dépasse. */
  function placerChoix() {
    if (!choixOuvert) return;
    var b = choixOuvert.hote.getBoundingClientRect();
    var p = choixOuvert.panneau;
    var h = p.offsetHeight;
    var w = p.offsetWidth;
    var marge = 8;

    var dessus = b.top - marge - h >= marge;
    p.style.top = (dessus ? b.top - marge - h : Math.min(b.bottom + marge, window.innerHeight - h - marge)) + 'px';
    p.style.transformOrigin = dessus ? 'bottom right' : 'top right';

    var gauche = b.right - w;
    gauche = Math.max(marge, Math.min(gauche, window.innerWidth - w - marge));
    p.style.left = gauche + 'px';
  }

  function ouvrirChoix(hote, modele) {
    fermerChoix();

    var panneau = document.createElement('div');
    panneau.className = 'banco-choix';
    panneau.setAttribute('role', 'group');
    panneau.appendChild(modele.content.cloneNode(true));
    /* invisible le temps d'être mesuré : sans cela le panneau apparaîtrait
       une image en haut à gauche avant d'être placé */
    panneau.style.visibility = 'hidden';
    document.body.appendChild(panneau);

    choixOuvert = { hote: hote, panneau: panneau };
    hote.setAttribute('aria-expanded', 'true');
    placerChoix();
    panneau.style.visibility = '';

    panneau.addEventListener('click', function (e) {
      var option = e.target.closest('[data-banco-quick-add]');
      if (!option) return;
      e.preventDefault();
      e.stopPropagation();
      ajouterAuPanier(option, hote, parseInt(option.dataset.bancoQuickAdd, 10));
    });

    var premiere = panneau.querySelector('[data-banco-quick-add]');
    if (premiere) premiere.focus();

    window.addEventListener('scroll', placerChoix, true);
    window.addEventListener('resize', placerChoix);
  }

  /* Fermetures : un clic ailleurs, Échap. Posés une fois pour toutes — le
     panneau, lui, va et vient. */
  document.addEventListener('click', function (e) {
    if (!choixOuvert) return;
    if (choixOuvert.panneau.contains(e.target) || choixOuvert.hote.contains(e.target)) return;
    fermerChoix();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !choixOuvert) return;
    var hote = choixOuvert.hote;
    fermerChoix();
    hote.focus();
  });

  function initQuickAdd(root) {
    (root || document).querySelectorAll('[data-banco-quick-add], [data-banco-choix]').forEach(function (btn) {
      if (btn.bancoQuickAdd) return;
      btn.bancoQuickAdd = true;
      btn.addEventListener('click', function (e) {
        /* le bouton vit dans le lien de la carte : ne pas naviguer */
        e.preventDefault();
        e.stopPropagation();

        if (btn.hasAttribute('data-banco-choix')) {
          if (choixOuvert && choixOuvert.hote === btn) { fermerChoix(); return; }
          /* le <template> suit le bouton dans le même parent */
          var modele = btn.parentElement && btn.parentElement.querySelector('[data-banco-choix-modele]');
          if (modele) ouvrirChoix(btn, modele);
          return;
        }

        ajouterAuPanier(btn, btn, parseInt(btn.dataset.bancoQuickAdd, 10));
      });
    });
  }

  /* ---------------- Initialisation ---------------- */
  function initAll(root) {
    initReveals(root);
    initCounters(root);
    initParallax(root);
    initMagnetic(root);
    initQuickAdd(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Ré-initialisation dans l'éditeur de thème Shopify */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
